<template>
  <v-container>
    <v-card>
      <v-tabs v-model="tab" color="primary">
        <v-tab value="usuarios">Usuarios</v-tab>
        <v-tab value="roles">Roles</v-tab>
      </v-tabs>
      <v-divider />

      <v-window v-model="tab">
        <v-window-item value="usuarios">
          <v-data-table :headers="headersUsuarios" :items="usuarios" item-key="id">
            <template v-slot:[`item.roles`]="{ item }">
              <v-select
                :model-value="item.roles"
                :items="nombresRoles"
                :disabled="item.id === $store.state.user?.id"
                multiple
                chips
                closable-chips
                density="compact"
                hide-details
                variant="outlined"
                @update:model-value="cambiarRoles(item, $event)"
              />
            </template>
          </v-data-table>
        </v-window-item>

        <v-window-item value="roles">
          <v-toolbar flat>
            <v-spacer />
            <v-btn color="primary" variant="flat" class="me-4" @click="abrirRol()">Nuevo Rol</v-btn>
          </v-toolbar>
          <v-data-table :headers="headersRoles" :items="roles" item-key="id">
            <template v-slot:[`item.menus`]="{ item }">
              <v-chip v-if="item.name === ROL_ADMIN" size="small" color="primary">Todos</v-chip>
              <v-chip v-else v-for="p in item.permisos.filter(esMenu)" :key="p" size="small" variant="outlined"
                :prepend-icon="p.startsWith('movil.') ? 'mdi-cellphone' : 'mdi-monitor'" class="me-1 my-1">
                {{ descripcionPermiso(p) }}
              </v-chip>
            </template>
            <template v-slot:[`item.permisos`]="{ item }">
              <v-chip v-if="item.name === ROL_ADMIN" size="small" color="primary">Todos</v-chip>
              <v-chip v-else v-for="p in item.permisos.filter(x => !esMenu(x))" :key="p" size="small" class="me-1 my-1">
                {{ descripcionPermiso(p) }}
              </v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <template v-if="item.name !== ROL_ADMIN">
                <v-icon class="me-2" size="small" @click="abrirRol(item)">mdi-pencil</v-icon>
                <v-icon size="small" color="red" @click="confirmarEliminar(item)">mdi-delete</v-icon>
              </template>
            </template>
          </v-data-table>
        </v-window-item>
      </v-window>
    </v-card>

    <v-dialog v-model="dialogRol" max-width="900px" persistent scrollable>
      <v-card>
        <v-card-title>{{ rolEditado.id ? 'Editar Rol' : 'Nuevo Rol' }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="rolEditado.name" label="Nombre del rol" />
          <v-row>
            <v-col cols="12" md="6">
              <div class="text-subtitle-1 font-weight-medium">Menús visibles</div>
              <div class="text-caption text-medium-emphasis mb-2">
                Al marcar un menú se agrega el permiso que su pantalla necesita.
              </div>
              <v-alert v-if="menusSinPermiso.length" type="warning" variant="tonal" density="compact" class="mb-2">
                Hay {{ menusSinPermiso.length }} menú(s) que aún no existen en el servidor
                ({{ menusSinPermiso.join(', ') }}). Ejecuta
                <code>php artisan db:seed --class=RolesPermisosSeeder</code> para poder asignarlos.
              </v-alert>
              <template v-for="seccion in seccionesMenu" :key="seccion.titulo">
                <div class="text-caption font-weight-bold mt-2">{{ seccion.titulo }}</div>
                <v-checkbox
                  v-for="item in seccion.items"
                  :key="item.menu"
                  :model-value="rolEditado.permisos.includes(item.menu)"
                  :label="item.titulo"
                  :messages="faltaRequerido(item) ? 'Sin permiso para usar esta pantalla' : ''"
                  :color="faltaRequerido(item) ? 'orange' : 'primary'"
                  density="compact"
                  @update:model-value="marcarMenu(item, $event)"
                />
              </template>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-subtitle-1 font-weight-medium mb-2">Permisos</div>
              <v-checkbox
                v-for="p in acciones"
                :key="p.name"
                v-model="rolEditado.permisos"
                :value="p.name"
                :label="p.descripcion"
                :disabled="SOLO_ADMIN.includes(p.name)"
                :messages="SOLO_ADMIN.includes(p.name) ? 'Exclusivo del rol Administrador' : ''"
                :hide-details="!SOLO_ADMIN.includes(p.name)"
                density="compact"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogRol = false">Cancelar</v-btn>
          <v-btn color="primary" variant="flat" :disabled="!rolEditado.name" @click="guardar">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogEliminar" width="auto" persistent>
      <v-card max-width="400" prepend-icon="mdi-delete" title="Eliminar Rol"
        :text="`¿Eliminar el rol ${rolEditado.name}? Los usuarios que lo tengan perderán esos permisos.`">
        <template v-slot:actions>
          <v-spacer />
          <v-btn color="blue-darken-1" text @click="dialogEliminar = false">Cancelar</v-btn>
          <v-btn color="red-darken-1" text @click="eliminar">Eliminar</v-btn>
        </template>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { MENU, MENU_MOVIL, tienePermiso } from '@/menu';

export default {
  data() {
    return {
      ROL_ADMIN: 'Administrador',
      MENU,
      MENU_MOVIL,
      // Debe coincidir con App\Support\Permisos::SOLO_ADMIN (el backend también lo impide).
      SOLO_ADMIN: ['solicitudes-gasto.aprobar'],
      tab: 'usuarios',
      dialogRol: false,
      dialogEliminar: false,
      rolEditado: { id: null, name: '', permisos: [] },
      headersUsuarios: [
        { title: 'Nombre', value: 'name' },
        { title: 'Correo', value: 'email' },
        { title: 'Roles', value: 'roles', sortable: false, width: '40%' },
      ],
      headersRoles: [
        { title: 'Rol', value: 'name' },
        { title: 'Usuarios', value: 'users_count' },
        { title: 'Menús', value: 'menus', sortable: false },
        { title: 'Permisos', value: 'permisos', sortable: false },
        { title: 'Acciones', value: 'actions', sortable: false },
      ],
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    ...mapGetters('roles', ['roles', 'permisos', 'usuarios']),
    // Solo se ofrecen los menús cuyo permiso ya existe en el backend
    nombresPermisos() {
      return new Set(this.permisos.map(p => p.name));
    },
    seccionesMenu() {
      return [...MENU, MENU_MOVIL]
        .map(s => ({ ...s, items: s.items.filter(i => this.nombresPermisos.has(i.menu)) }))
        .filter(s => s.items.length);
    },
    menusSinPermiso() {
      if (!this.permisos.length) return [];
      return [...MENU, MENU_MOVIL].flatMap(s => s.items)
        .filter(i => !this.nombresPermisos.has(i.menu))
        .map(i => i.titulo);
    },
    acciones() {
      return this.permisos.filter(p => p.tipo === 'accion');
    },
    nombresRoles() {
      return this.roles.map(r => r.name);
    },
  },
  created() {
    this.fetchRoles();
    this.fetchPermisos();
    this.fetchUsuarios();
  },
  methods: {
    ...mapActions('roles', ['fetchRoles', 'fetchPermisos', 'fetchUsuarios', 'guardarRol', 'deleteRol', 'asignarRoles']),
    notificar(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
    notificarError(e) {
      this.notificar(e.response?.data?.message || 'Ocurrió un error', 'red');
    },
    esMenu(nombre) {
      return nombre.startsWith('menu.') || nombre.startsWith('movil.');
    },
    faltaRequerido(item) {
      return this.rolEditado.permisos.includes(item.menu)
        && !tienePermiso(this.rolEditado.permisos, item.requiere);
    },
    marcarMenu(item, marcado) {
      const permisos = this.rolEditado.permisos.filter(p => p !== item.menu);
      if (marcado) {
        permisos.push(item.menu);
        if (!tienePermiso(permisos, item.requiere)) {
          permisos.push([].concat(item.requiere)[0]);
        }
      }
      this.rolEditado.permisos = permisos;
    },
    descripcionPermiso(nombre) {
      return this.permisos.find(p => p.name === nombre)?.descripcion || nombre;
    },
    async cambiarRoles(usuario, roles) {
      try {
        await this.asignarRoles({ id: usuario.id, roles });
        this.notificar(`Roles de ${usuario.name} actualizados`);
      } catch (e) {
        this.notificarError(e);
        this.fetchUsuarios();
      }
    },
    abrirRol(rol = null) {
      this.rolEditado = rol
        ? { id: rol.id, name: rol.name, permisos: [...rol.permisos] }
        : { id: null, name: '', permisos: [] };
      this.dialogRol = true;
    },
    async guardar() {
      try {
        await this.guardarRol({
          ...this.rolEditado,
          permisos: this.rolEditado.permisos.filter(p => this.nombresPermisos.has(p)),
        });
        this.dialogRol = false;
        this.notificar('Rol guardado');
      } catch (e) {
        this.notificarError(e);
      }
    },
    confirmarEliminar(rol) {
      this.rolEditado = { ...rol };
      this.dialogEliminar = true;
    },
    async eliminar() {
      try {
        await this.deleteRol(this.rolEditado.id);
        this.notificar('Rol eliminado');
      } catch (e) {
        this.notificarError(e);
      }
      this.dialogEliminar = false;
    },
  },
};
</script>
